import { VisualizationSpec } from 'vega-embed';
import { processPieChart } from './pie';
import { processLineChart } from './line';
import { TopLevelSpec } from 'vega-lite/build/spec/index';
import {
    ChannelDef,
    DatumDef,
    Field,
    FieldDefBase,
    SecondaryFieldDef,
    TypedFieldDef,
} from 'vega-lite/build/channeldef';
import { processMultiChart } from './multi';
import { BaseSpec } from 'vega-lite/build/spec/base';
import { FacetedUnitSpec, NormalizedUnitSpec } from 'vega-lite/build/spec/unit';
import { hasOwnProperty, isObject } from 'vega-util';

const hasProperty = function <T>(obj: T, key: string | number | symbol): key is keyof T {
    return isObject(obj) && hasOwnProperty(obj, key) && (obj as any)[key] !== undefined;
};

const isUnitSpec = function (spec: BaseSpec): spec is FacetedUnitSpec<any> | NormalizedUnitSpec {
    return hasProperty(spec, 'mark');
};
const isFieldDef = function <F extends Field>(
    channelDef: Partial<ChannelDef<F>> | FieldDefBase<F> | DatumDef<F, any>,
): channelDef is FieldDefBase<F> | TypedFieldDef<F> | SecondaryFieldDef<F> {
    return hasProperty(channelDef, 'field') || (channelDef as any)?.aggregate === 'count';
};

export interface ChartProcessorContext {
    spec: VisualizationSpec;
    width?: number;
    height?: number;
    colors: string[];
    isBig: boolean;
}

export function isVegaLiteSpec(spec: VisualizationSpec): boolean {
    return 'encoding' in spec;
}

const MAX_TITLE_LENGTH = 30;

function getMarkType(spec: VisualizationSpec): string | undefined {
    if ('mark' in spec) {
        const mark = spec.mark;
        const type = typeof mark === 'string' ? mark : mark?.type;
        if (typeof type === 'string') {
            if (type === 'arc') return 'pie';
            return type;
        }
    }

    if ('marks' in spec && Array.isArray((spec as any).marks)) {
        const markType = (spec as any).marks[0]?.type;
        if (markType === 'arc') return 'pie';
        if (markType === 'line') return 'line';
        if (markType === 'rect') return 'bar';
    }

    if ('layer' in spec && Array.isArray((spec as any).layer)) {
        return 'multi';
    }

    return 'unknown';
}

export function processChartConfig(ctx: ChartProcessorContext): VisualizationSpec {
    if (isVegaLiteSpec(ctx.spec)) {
        return processVegaLiteChart(ctx);
    }

    return processVegaChart(ctx);
}

export function processVegaLiteChart({ spec, width, height, colors, isBig }: ChartProcessorContext): TopLevelSpec {
    const newSpec = { ...spec } as TopLevelSpec;
    const mark = getMarkType(spec);

    if (!isBig) {
        if (typeof newSpec.title === 'string') {
            if (newSpec.title.length > MAX_TITLE_LENGTH) {
                newSpec.title = newSpec.title.slice(0, MAX_TITLE_LENGTH - 1) + '…';
            }
        } else if (
            typeof newSpec.title === 'object' &&
            newSpec.title !== null &&
            'text' in newSpec.title &&
            typeof newSpec.title.text === 'string'
        ) {
            const raw = newSpec.title.text;
            if (raw.length > MAX_TITLE_LENGTH) {
                newSpec.title.text = raw.slice(0, MAX_TITLE_LENGTH - 1) + '…';
            }
        }

        newSpec.config = {
            ...(newSpec.config || {}),
            title: {
                ...(newSpec.config?.title || {}),
                fontSize: 10,
            },
            axis: {
                ...(newSpec.config?.axis || {}),
                titleFontSize: 10,
            },
        };
    }

    if (isUnitSpec(newSpec) && newSpec.encoding && newSpec.encoding.color && isFieldDef(newSpec.encoding.color)) {
        const colorField = newSpec.encoding.color.field;
        let domain: (string | number)[] | undefined;

        if (newSpec.data && 'values' in newSpec.data && Array.isArray(newSpec.data.values)) {
            domain = Array.from(
                new Set(
                    newSpec.data.values
                        .map((d: any) => d?.[colorField])
                        .filter((v: any) => typeof v === 'string' || typeof v === 'number'),
                ),
            );
        }

        newSpec.encoding.color = {
            ...newSpec.encoding.color,
            scale: {
                type: 'ordinal',
                ...(domain ? { domain } : {}),
                range: colors,
            },
        };
    }

    if (isUnitSpec(newSpec) && width) {
        newSpec.width = width;
    }

    if (isUnitSpec(newSpec) && height) {
        newSpec.height = height;
    }

    if (isUnitSpec(newSpec)) {
        newSpec.autosize = {
            type: 'fit',
            contains: 'padding',
            resize: true,
        };
    }

    if (isUnitSpec(newSpec) && newSpec.encoding?.color && isFieldDef(newSpec.encoding.color)) {
        newSpec.encoding.color.legend = {
            ...(newSpec.encoding.color.legend || {}),
            orient: 'bottom',
        };
    }

    if (
        isUnitSpec(newSpec) &&
        newSpec.encoding?.color &&
        !isFieldDef(newSpec.encoding.color) &&
        'value' in newSpec.encoding.color &&
        typeof newSpec.encoding.color.value === 'string' &&
        colors?.length
    ) {
        const fallbackField = '__color_patch__';

        if (newSpec.data && 'values' in newSpec.data && Array.isArray(newSpec.data.values)) {
            newSpec.data.values = newSpec.data.values.map((d) => ({
                ...d,
                [fallbackField]: 'default',
            }));
        }

        newSpec.encoding.color = {
            field: fallbackField,
            type: 'nominal',
        };
    }

    if (
        isUnitSpec(newSpec) &&
        newSpec.encoding?.color &&
        isFieldDef(newSpec.encoding.color) &&
        newSpec.encoding.color.field === '__color_patch__'
    ) {
        newSpec.encoding.color.legend = null;
    }

    switch (mark) {
        case 'arc':
        case 'pie':
            return processPieChart({ spec: newSpec, width, height, colors, isBig }) as TopLevelSpec;
        case 'multi':
            return processMultiChart({ spec: newSpec, width, height, colors, isBig }) as TopLevelSpec;
        default:
            return newSpec;
    }
}

export function processVegaChart({ colors, width, height, spec, isBig }: ChartProcessorContext): VisualizationSpec {
    const mark = getMarkType(spec);
    const baseSpec = { ...spec } as VisualizationSpec;

    try {
        if (
            'encoding' in baseSpec &&
            typeof baseSpec.encoding === 'object' &&
            baseSpec.encoding !== null &&
            'x' in baseSpec.encoding &&
            baseSpec.encoding.x &&
            typeof baseSpec.encoding.x === 'object' &&
            'field' in baseSpec.encoding.x
        ) {
            baseSpec.encoding = {
                ...baseSpec.encoding,
                x: {
                    ...baseSpec.encoding.x,
                    axis: {
                        ...(baseSpec.encoding.x as any).axis,
                        labelAngle: 45,
                    },
                },
            };
        }

        if (
            'encoding' in baseSpec &&
            baseSpec.encoding &&
            typeof baseSpec.encoding === 'object' &&
            'color' in baseSpec.encoding &&
            baseSpec.encoding.color &&
            typeof baseSpec.encoding.color === 'object' &&
            'field' in baseSpec.encoding.color
        ) {
            baseSpec.encoding.color = {
                ...baseSpec.encoding.color,
                scale: {
                    ...(baseSpec.encoding.color.scale || {}),
                    range: colors,
                },
            };
        }

        if (Array.isArray((baseSpec as any).scales)) {
            (baseSpec as any).scales = (baseSpec as any).scales.map((scale: any) => {
                if (scale.name === 'color') {
                    return {
                        ...scale,
                        range: colors,
                    };
                }
                return scale;
            });
        }

        delete (baseSpec as any).height;
        delete (baseSpec as any).padding;
        delete (baseSpec as any).autosize;

        if (width) {
            if ('width' in baseSpec) {
                baseSpec.width = width * 0.65;
            }
        }

        if (Array.isArray((baseSpec as any).legends)) {
            (baseSpec as any).legends = (baseSpec as any).legends.map((legend: any) => ({
                ...legend,
                orient: 'bottom',
            }));
        }

        if (Array.isArray((baseSpec as any).axes) && Array.isArray((baseSpec as any).scales)) {
            const scaleMap = new Map<string, string>();

            for (const scale of (baseSpec as any).scales) {
                if (scale.name && scale.type) {
                    scaleMap.set(scale.name, scale.type);
                }
            }

            (baseSpec as any).axes = (baseSpec as any).axes.map((axis: any) => {
                const scaleType = scaleMap.get(axis.scale);

                const isQuantitative =
                    scaleType === 'linear' ||
                    scaleType === 'quantitative' ||
                    scaleType === 'log' ||
                    scaleType === 'pow' ||
                    scaleType === 'sqrt' ||
                    scaleType === 'symlog';

                if (isQuantitative) {
                    return {
                        ...axis,
                        format: '.2s',
                        formatType: 'number',
                    };
                }

                return axis;
            });
        }

        if (!isBig) {
            if (typeof baseSpec.title === 'string') {
                if (baseSpec.title.length > MAX_TITLE_LENGTH) {
                    baseSpec.title = baseSpec.title.slice(0, MAX_TITLE_LENGTH - 1) + '…';
                }
            } else if (
                typeof baseSpec.title === 'object' &&
                baseSpec.title !== null &&
                'text' in baseSpec.title &&
                typeof baseSpec.title.text === 'string'
            ) {
                const rawTitle = baseSpec.title.text;
                if (rawTitle.length > MAX_TITLE_LENGTH) {
                    baseSpec.title.text = rawTitle.slice(0, MAX_TITLE_LENGTH - 1) + '…';
                }
            }
        }

        const config = { ...(baseSpec.config || {}) };

        if (!isBig) {
            config.title = {
                ...(config.title || {}),
                fontSize: 10,
            };

            config.axis = {
                ...(config.axis || {}),
                labelFontSize: 10,
                titleFontSize: 10,
            } as any;
        }

        baseSpec.config = config;
    } catch (error) {
        console.error('Invalid chart specification:', error);
        return spec;
    }

    switch (mark) {
        case 'line':
            return processLineChart({ spec: baseSpec, width, height, colors, isBig });
        case 'arc':
        case 'pie':
            return processPieChart({ spec: baseSpec, width, height, colors, isBig });
        default:
            return baseSpec;
    }
}
